import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  ChevronDown,
  BookOpen,
  ClipboardCheck,
  CreditCard,
  GitBranch,
  HelpCircle,
  Layers,
  Inbox,
  LayoutDashboard,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Search,
  Settings as SettingsIcon,
  Ticket,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth, usePermissions } from '../hooks/useAuth'
import { useUnreadNotificationCount, useNotifications, useMarkAllRead } from '../hooks/useNotifications'
import { usePipelineUpdates } from '../hooks/usePipelineUpdates'
import { helpApi } from '../lib/api'
import { APPOINTMENT_NAV_ROLES, MERCHANT_NAV_ROLES } from '../lib/navAccess'
import { CommandPalette } from './CommandPalette'
import { NotificationMessage } from './NotificationMessage'
import agmsLogo from '../assets/logos/agms-logo-on-white.svg'

interface LayoutProps {
  children: React.ReactNode
}

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  badge?: number
}

const COLLAPSE_KEY = 'agms-sidebar-collapsed'

/**
 * Application shell: collapsible sidebar, top bar, and global search.
 */
export function Layout({ children }: LayoutProps) {
  usePipelineUpdates()

  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, isLoggingOut } = useAuth()
  const { canViewReports, canViewChargebackPortfolio, canViewTickets, hasRole } = usePermissions()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try {
      setCollapsed(window.localStorage.getItem(COLLAPSE_KEY) === '1')
    } catch {
      setCollapsed(false)
    }
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setUserMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const standardShortcut = (event.metaKey || event.ctrlKey)
        && !event.altKey
        && !event.shiftKey
        && event.key.toLowerCase() === 'k'
      if (!standardShortcut) return
      // Capture phase so the letter is not inserted into a focused field.
      event.preventDefault()
      setSearchOpen((open) => !open)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        // Preference is visual only.
      }
      return next
    })
  }

  const isActive = (path: string) => {
    if (path === '/help') {
      return location.pathname === '/help'
        || (location.pathname.startsWith('/help/')
          && !location.pathname.startsWith('/help/review')
          && !location.pathname.startsWith('/help/gaps')
          && !location.pathname.startsWith('/help/health'))
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  const shortcutLabel = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘K'
    : 'Ctrl K'

  const initials = user?.name
    ?.split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'A'

  const roleLabel = user?.role.replace('_', ' ') ?? ''
  const showChargebacks = canViewChargebackPortfolio()
  const showTickets = canViewTickets()
  const showInbox = hasRole(['operations', 'management', 'support', 'super_admin'])
  const showReports = canViewReports()
  const showArticleQueue = hasRole(['management', 'super_admin'])
  const showSettings = hasRole(['super_admin'])
  const { data: pendingReviewData } = useQuery({
    queryKey: ['help-review-count'],
    queryFn: () => helpApi.listArticles({ status: 'pending_review', per_page: 1 }),
    enabled: showArticleQueue,
    staleTime: 60 * 1000,
  })
  const pendingReviewCount = pendingReviewData?.total ?? 0

  const mainItems: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ...(hasRole(['sales_rep', 'operations', 'management', 'super_admin'])
      ? [{ to: '/leads', label: 'Leads', icon: Users }]
      : []),
    ...(hasRole(['operations', 'management', 'finance', 'super_admin'])
      ? [{ to: '/pipeline', label: 'Deployment', icon: GitBranch }]
      : []),
    ...(hasRole([...MERCHANT_NAV_ROLES])
      ? [{ to: '/merchants', label: 'Merchants', icon: Building2 }]
      : []),
    ...(hasRole(['sales_rep', ...MERCHANT_NAV_ROLES])
      ? [{ to: '/account-groups', label: 'Account groups', icon: Layers }]
      : []),
    ...(hasRole(['sales_rep', 'operations', 'management', 'finance', 'super_admin'])
      ? [{ to: '/statement-analysis', label: 'Statement Analysis', icon: Receipt }]
      : []),
    ...(hasRole([...APPOINTMENT_NAV_ROLES])
      ? [{ to: '/appointments', label: 'Appointments', icon: Calendar }]
      : []),
    ...(showReports ? [{ to: '/reports', label: 'Reports', icon: BarChart3 }] : []),
  ]

  const managementItems: NavItem[] = [
    ...(showChargebacks ? [{ to: '/chargebacks', label: 'Chargebacks', icon: CreditCard }] : []),
    ...(showTickets ? [{ to: '/tickets', label: 'Tickets', icon: Ticket }] : []),
    ...(showInbox ? [{ to: '/inbox', label: 'Inbox', icon: Inbox }] : []),
  ]

  const helpItems: NavItem[] = [
    { to: '/help', label: 'Help', icon: HelpCircle },
    ...(showArticleQueue
      ? [
          { to: '/help/review', label: 'Article Queue', icon: ClipboardCheck, badge: pendingReviewCount },
          { to: '/help/gaps', label: 'Knowledge Gaps', icon: BookOpen },
          { to: '/help/health', label: 'Article Health', icon: Activity },
        ]
      : []),
  ]

  const systemItems: NavItem[] = [
    ...(showSettings ? [{ to: '/settings', label: 'Settings', icon: SettingsIcon }] : []),
  ]

  const linkClass = (path: string) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      isActive(path)
        ? 'bg-white/10 text-white shadow-[inset_3px_0_0_0_#ffc200]'
        : 'text-slate-300 hover:bg-white/5 hover:text-white'
    }`

  const renderItems = (items: NavItem[]) => items.map((item) => {
    const Icon = item.icon
    return (
      <Link key={item.to} to={item.to} className={linkClass(item.to)} title={item.label}>
        <Icon className="h-5 w-5 shrink-0" />
        <span className={collapsed ? 'truncate lg:sr-only' : 'truncate'}>{item.label}</span>
        {!collapsed && item.badge && item.badge > 0 ? (
          <span className="ml-auto rounded-full bg-accent-500 px-2 py-0.5 text-xs font-semibold text-brand-950">
            {item.badge}
          </span>
        ) : null}
      </Link>
    )
  })

  const renderGroup = (label: string, items: NavItem[]) => {
    if (items.length === 0) return null
    const groupId = `nav-group-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    return (
      <div role="group" aria-labelledby={groupId} className="space-y-1">
        <div id={groupId} className={`px-3 text-xs font-semibold uppercase tracking-wider text-slate-300 ${collapsed ? 'lg:sr-only' : ''}`}>
          {label}
        </div>
        {renderItems(items)}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="sticky top-0 z-40 border-b border-line bg-white">
        <div className="h-0.5 bg-accent-500" />
        <div className="flex h-14 items-center gap-3 px-4">
          <button
            type="button"
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            onClick={() => setMobileOpen((open) => !open)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/dashboard" className="shrink-0">
            <img src={agmsLogo} alt="AGMS" className="h-7 w-auto" />
          </Link>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="ml-auto flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-line bg-canvas px-3 text-sm text-muted transition hover:border-brand-200 hover:bg-white md:ml-6 md:max-w-xl"
            aria-label="Search leads and merchants"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="truncate">Search leads and merchants</span>
            <kbd className="ml-auto hidden rounded border border-line bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-500 sm:inline">
              {shortcutLabel}
            </kbd>
          </button>
          <NotificationBell />
          <div ref={userMenuRef} className="relative">
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-slate-50"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label="Account menu"
              onClick={() => setUserMenuOpen((open) => !open)}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
                {initials}
              </span>
              <span className="hidden min-w-0 text-left sm:block">
                <span className="block max-w-32 truncate text-sm font-semibold text-ink" title={user?.name}>{user?.name}</span>
                <span className="block truncate text-xs capitalize text-muted">{roleLabel}</span>
              </span>
              <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" />
            </button>
            {userMenuOpen ? (
              <div role="menu" className="absolute right-0 mt-2 w-52 overflow-hidden rounded-xl border border-line bg-white shadow-pop">
                <div className="border-b border-line px-3 py-2">
                  <div className="truncate text-sm font-semibold text-ink">{user?.name}</div>
                  <div className="truncate text-xs capitalize text-muted">{roleLabel}</div>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className="flex">
        {mobileOpen ? (
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-30 bg-brand-950/40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}
        <aside
          className={`fixed bottom-0 left-0 top-[3.625rem] z-30 flex w-64 flex-col bg-brand-950 transition-transform lg:sticky lg:top-[3.625rem] lg:h-[calc(100vh-3.625rem)] lg:translate-x-0 ${
            collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64'
          } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          <nav className="flex-1 space-y-6 overflow-y-auto p-3" aria-label="Primary navigation">
            {renderGroup('Main', mainItems)}
            {renderGroup('Management', managementItems)}
            {renderGroup('Help hub', helpItems)}
            {renderGroup('System', systemItems)}
          </nav>
          <div className="border-t border-white/10 p-3">
            <button
              type="button"
              onClick={toggleCollapsed}
              className="hidden w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white lg:flex"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeftOpen className="h-5 w-5 shrink-0" /> : <PanelLeftClose className="h-5 w-5 shrink-0" />}
              <span className={collapsed ? 'sr-only' : ''}>{collapsed ? 'Expand' : 'Collapse'}</span>
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1">
          <div className="p-page">{children}</div>
        </main>
      </div>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  )
}

/**
 * Notification bell with count badge and dropdown.
 */
function NotificationBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { data: countData } = useUnreadNotificationCount()
  const { data: notifData } = useNotifications(open)
  const markAllRead = useMarkAllRead()

  const count = countData?.count ?? 0
  const notifications = notifData?.notifications ?? []

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleToggle = () => {
    const willOpen = !open
    setOpen(willOpen)
    if (willOpen && count > 0) {
      markAllRead.mutate()
    }
  }

  const formatTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleToggle}
        className="relative rounded-lg border border-line bg-white p-2 text-slate-500 shadow-sm transition-colors hover:text-brand-700"
        title="Notifications"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-line bg-white shadow-pop">
          <div className="border-b border-line px-4 py-3">
            <h3 className="text-sm font-semibold text-ink">Notifications</h3>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted">No notifications</p>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`border-b border-slate-50 px-4 py-3 last:border-0 ${
                    !n.read_at ? 'bg-brand-50/60' : ''
                  }`}
                >
                  <NotificationMessage notification={n} />
                  <p className="mt-0.5 text-xs text-muted">{formatTime(n.created_at)}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Layout
